import { VueComponentUpload as upload } from '@kvass/storage'
import FlatfinderAPI from '@/api/Flatfinder'
import fetch from '@/fetch'
import { LoadImage } from '@/utils'
import { MAX_WIDTH, uploadOptions } from '@/components/Flatfinder/constants'

const ImageExtensions = ['png', 'jpg', 'jpeg', 'gif', 'webp', 'bmp']

const SEASON_MONTHS = { spring: '03', summer: '06', autumn: '09', winter: '12' }
const TIME_REGEX = /^(\d{2})[-.]?(\d{2})$/ // sunlight filename time: 09.00 / 09-00 / 0900 → 09:00
const NO_FRAME_FOLDER_KEY = '__noframes'

const TYPE_BY_FOLDER = { masks: 'mask', floorplans: 'floorplan', sunlight: 'sunlight' }
const RESERVED = ['frames', ...Object.keys(TYPE_BY_FOLDER)]

const UploadImage = (file, rename) => upload(file, () => {}, { ...uploadOptions, rename })
const getFilename = file => file.name.split('/').pop().split('.').slice(0, -1).join('.')

const LookupItems = (id, items) => {
  const url = new URL('/api/flatfinder/lookup', location.origin)
  url.searchParams.append('project', id)
  items.forEach(item => url.searchParams.append('items', item.file.name))
  return fetch(url.toString()).then(res => res.json())
}

const resolveSunDate = folder => {
  const month = SEASON_MONTHS[(folder || '').toLowerCase()]
  if (!month) return null
  return `${new Date().getFullYear()}-${month}-01`
}

const parseTime = file => {
  const match = getFilename(file).match(TIME_REGEX)
  if (!match) return null
  const [, hours, minutes] = match
  if (Number(hours) > 23 || Number(minutes) > 59) return null
  return `${hours}:${minutes}`
}

const classifyFile = folderParts => {
  const lower = folderParts.map(part => part.toLowerCase())

  const firstReserved = lower.findIndex(part => RESERVED.includes(part))
  const flatfinderParts = firstReserved === -1 ? folderParts : folderParts.slice(0, firstReserved)

  const framesIndex = lower.indexOf('frames')
  const frameKey = framesIndex === -1 ? NO_FRAME_FOLDER_KEY : folderParts[framesIndex + 1]

  const typeFolder = Object.keys(TYPE_BY_FOLDER).find(folder => lower.includes(folder))
  const type = TYPE_BY_FOLDER[typeFolder] || 'image'

  const sunlightIndex = lower.indexOf('sunlight')
  const sunDate = type === 'sunlight' ? resolveSunDate(folderParts[sunlightIndex + 1]) : null

  return { flatfinderParts, frameKey, type, sunDate }
}

const TransformCoordinates = (data, sourceSize, targetSize) => {
  const scaleX = targetSize.width / sourceSize.width
  const scaleY = targetSize.height / sourceSize.height

  return {
    ...data,
    geometry: {
      ...data.geometry,
      coordinates: data.geometry.coordinates.map(ring => ring.map(([x, y]) => [x * scaleX, y * scaleY])),
    },
  }
}

async function ExtractMasks(files, targetSize) {
  return Promise.all(
    (files || []).map(async file => {
      const img = await LoadImage(file)
      const url = URL.createObjectURL(file)
      const data = await ExtractPolygonFromMask(url)
      URL.revokeObjectURL(url)

      return {
        name: getFilename(file),
        file,
        data: data ? TransformCoordinates(data, { width: img.width, height: img.height }, targetSize) : null,
      }
    }),
  )
}

async function ProcessSunlight(entries, frameImage) {
  const conditions = []
  const skipped = []
  const reference = frameImage && frameImage.dimensions ? frameImage.dimensions : null

  for (const { file, date } of entries || []) {
    const ext = file.name.split('.').pop().toLowerCase()
    const time = ImageExtensions.includes(ext) ? parseTime(file) : null
    if (!date || !time) {
      skipped.push(file)
      continue
    }

    const image = await UploadImage(file)
    const dims = image && image.dimensions ? image.dimensions : []
    if (reference && (dims[0] !== reference[0] || dims[1] !== reference[1])) {
      skipped.push(file)
      continue
    }

    conditions.push({ image, date, time })
  }

  return { conditions, skipped }
}

async function ProcessFrame(group) {
  const imageFile = (group.images || [])[0]
  if (!imageFile) return null

  const img = await LoadImage(imageFile)
  const scale = img.width > MAX_WIDTH ? MAX_WIDTH / img.width : 1
  const targetSize = { width: img.width * scale, height: img.height * scale }

  const image = await UploadImage(imageFile)
  const masks = await ExtractMasks(group.masks, targetSize)
  const sun = await ProcessSunlight(group.sunlight, image)

  return { image, masks, sunlightConditions: sun.conditions, skippedSun: sun.skipped }
}

const pickImageFiles = () =>
  new Promise(resolve => {
    const input = document.createElement('input')
    input.type = 'file'
    input.multiple = true
    input.accept = ImageExtensions.map(ext => `.${ext}`).join(',')
    input.onchange = e => resolve(Array.from(e.target.files))
    input.oncancel = () => resolve(null)
    input.click()
  })

export async function getItemsFromMasks({ dimensions, project }) {
  const files = await pickImageFiles()
  if (!files) return null

  const targetSize = { width: dimensions[0], height: dimensions[1] }
  const masks = await ExtractMasks(files, targetSize)
  const lookup = await LookupItems(project, masks)

  const items = []
  const unmatched = []

  masks.forEach(item => {
    const match = lookup[item.file.name]
    if (!match || !item.data) {
      unmatched.push(item.file.name)
      return
    }
    items.push({
      reference: {
        ref: { id: match.ref, template: null, name: match.title, level: 0 },
        onModel: match.onModel,
      },
      data: item.data,
    })
  })

  return { items, unmatched }
}

export function CreateFlatfindersFromUpload(projectId, options = {}) {
  const { onProgress = () => {} } = options

  const stats = new Proxy(
    {
      files: [],
      flatfinders: [],
      floorplans: [],
      masks: [],
      sunlight: [],
      skipped: [],
      processed: [],
    },
    {
      set: (target, key, value) => {
        target[key] = value
        onProgress({ ...target })
        return true
      },
    },
  )

  return new Promise((resolve, reject) => {
    const input = document.createElement('input')
    input.type = 'file'
    input.webkitdirectory = true

    input.onchange = async e => {
      const files = Array.from(e.target.files)
      stats.files = files.map(f => f.webkitRelativePath)

      const tree = {}
      const floorplans = []

      for (const file of files) {
        const parts = file.webkitRelativePath.split('/')
        if (parts.length < 2) {
          stats.skipped = [...stats.skipped, file.webkitRelativePath]
          continue
        }

        const folderParts = parts.slice(1, -1)
        const { flatfinderParts, frameKey, type, sunDate } = classifyFile(folderParts)

        if (type === 'floorplan') {
          floorplans.push(file)
          continue
        }

        if (frameKey === undefined) {
          stats.skipped = [...stats.skipped, file.webkitRelativePath]
          continue
        }

        // Ensure the flatfinder folder (root '') + all its ancestors exist.
        if (!tree['']) tree[''] = { name: parts[0], path: '', parentPath: null, frames: {} }
        for (let i = 0; i < flatfinderParts.length; i++) {
          const path = flatfinderParts.slice(0, i + 1).join('/')
          const parentPath = i > 0 ? flatfinderParts.slice(0, i).join('/') : ''
          if (!tree[path]) tree[path] = { name: flatfinderParts[i], path, parentPath, frames: {} }
        }

        const node = tree[flatfinderParts.join('/')]
        if (!node.frames[frameKey]) node.frames[frameKey] = { images: [], masks: [], sunlight: [] }
        const bucket = node.frames[frameKey]
        if (type === 'mask') bucket.masks.push(file)
        else if (type === 'sunlight') bucket.sunlight.push({ file, date: sunDate })
        else bucket.images.push(file)
      }

      // Sort by depth (root '' first, then by path segment count) so parents exist before children.
      const sortedPaths = Object.keys(tree).sort((a, b) => {
        const depthA = a === '' ? 0 : a.split('/').length
        const depthB = b === '' ? 0 : b.split('/').length
        return depthA - depthB
      })

      const frameGroups = node => {
        const numbered = Object.keys(node.frames).filter(key => key !== NO_FRAME_FOLDER_KEY)
        if (numbered.length) {
          return numbered.sort((a, b) => a.localeCompare(b, undefined, { numeric: true })).map(key => node.frames[key])
        }
        return node.frames[NO_FRAME_FOLDER_KEY] ? [node.frames[NO_FRAME_FOLDER_KEY]] : []
      }

      // Phase 1: Create all flatfinders with their frame image(s) + parent only (no items yet).
      // This ensures child flatfinders exist in the DB before parent item lookups run.
      const flatfinderIds = {} // path -> created flatfinder id
      const framesByPath = {} // path -> [{ image (uploaded), masks }] per frame, reused in phase 3

      for (const path of sortedPaths) {
        const { name, parentPath } = tree[path]
        const groups = frameGroups(tree[path])

        const frames = []
        for (const group of groups) {
          const result = await ProcessFrame(group)
          if (!result) continue
          frames.push({ image: result.image, masks: result.masks, sunlightConditions: result.sunlightConditions })
          const sunlightNames = result.sunlightConditions.map(condition => condition.image.name)
          stats.processed = [...stats.processed, result.image.name, ...sunlightNames]
          stats.sunlight = [...stats.sunlight, ...sunlightNames]
          if (result.skippedSun.length)
            stats.skipped = [...stats.skipped, ...result.skippedSun.map(f => f.webkitRelativePath)]
        }

        if (!frames.length) {
          stats.skipped = [
            ...stats.skipped,
            ...groups
              .flatMap(group => [...group.images, ...group.masks, ...group.sunlight.map(s => s.file)])
              .map(f => f.webkitRelativePath),
          ]
          continue
        }

        framesByPath[path] = frames
        const parentId = parentPath !== null ? flatfinderIds[parentPath] : undefined
        const payload = {
          name,
          project: projectId,
          frames: frames.map(frame => ({ image: frame.image, sunlightConditions: frame.sunlightConditions })),
          ...(parentId && { parent: parentId }),
        }
        const flatfinder = await FlatfinderAPI.mutate(payload, {
          transformPayload: true,
          arrayStrategy: 'default',
        })
        flatfinderIds[path] = flatfinder.id
        stats.flatfinders = [...stats.flatfinders, name]
      }

      // Single lookup for all masks (deduped by filename — the same unit repeats across frames)
      // + floorplans.
      const allMasks = Object.values(framesByPath).flatMap(frames => frames.flatMap(frame => frame.masks))
      const uniqueMasks = [...new Map(allMasks.map(mask => [mask.file.name, mask])).values()]
      const allLookupItems = [...uniqueMasks, ...floorplans.map(file => ({ file }))]
      const lookup = allLookupItems.length ? await LookupItems(projectId, allLookupItems) : {}

      // Phase 2: Upload floorplans, grouped by residential.
      const floorplanGroups = {}
      if (floorplans.length) {
        for (const file of floorplans) {
          const match = lookup[file.name]
          if (!match) {
            stats.skipped = [...stats.skipped, file.webkitRelativePath]
            continue
          }
          if (!floorplanGroups[match.ref]) floorplanGroups[match.ref] = []
          floorplanGroups[match.ref].push(file)
        }

        for (const [residentialId, floorplanFiles] of Object.entries(floorplanGroups)) {
          const uploaded = await Promise.all(floorplanFiles.map(file => UploadImage(file, getFilename(file))))
          await fetch(`/api/residential/${residentialId}/floorplans?strategy=replace&sort=true`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ items: uploaded }),
          })

          const floorplanNames = floorplanFiles.map(f => f.webkitRelativePath)
          stats.processed = [...stats.processed, ...floorplanNames]
          stats.floorplans = [...stats.floorplans, ...floorplanNames]
        }
      }

      // Build a flatfinder item from a mask, or null if it doesn't resolve. Names are collected
      // per frame and written to `stats` in one go — `stats` is a Proxy that notifies on
      // assignment, and one notification per mask would be thousands on a 360 import.
      const buildItem = (item, collected) => {
        const name = item.file.webkitRelativePath
        const match = lookup[item.file.name]
        if (!match || !item.data) {
          collected.skipped.push(name)
          return
        }
        collected.processed.push(name)
        collected.masks.push(name)
        const thumbnailType = floorplanGroups[match.ref] && floorplanGroups[match.ref].length ? 'floorplan' : 'cover'
        return {
          reference: { ref: match.ref, onModel: match.onModel },
          data: { ...item.data, thumbnailType },
        }
      }

      // Phase 3: Update each flatfinder with its per-frame items (child flatfinders now exist,
      // so references resolve correctly).
      for (const path of sortedPaths) {
        const id = flatfinderIds[path]
        if (!id) continue

        const frames = framesByPath[path]
        // Frames replace wholesale and `image` is required, so each frame's image is resent.
        const framesPayload = frames.map(frame => {
          const collected = { masks: [], skipped: [], processed: [] }
          const items = frame.masks.map(mask => buildItem(mask, collected)).filter(Boolean)

          stats.masks = [...stats.masks, ...collected.masks]
          stats.processed = [...stats.processed, ...collected.processed]
          if (collected.skipped.length) stats.skipped = [...stats.skipped, ...collected.skipped]

          return { image: frame.image, items, sunlightConditions: frame.sunlightConditions }
        })

        if (!framesPayload.some(frame => frame.items.length)) continue

        await FlatfinderAPI.mutate({ id, frames: framesPayload }, { transformPayload: true, arrayStrategy: 'default' })
      }

      resolve()
    }

    input.oncancel = () => reject()
    input.click()
  })
}

async function ExtractPolygonFromMask(imageSource, options = {}) {
  const { threshold = 128, simplifyTolerance = 2 } = options

  const image = await LoadImage(imageSource)
  const canvas = document.createElement('canvas')
  canvas.width = image.width
  canvas.height = image.height

  const ctx = canvas.getContext('2d')
  ctx.drawImage(image, 0, 0)

  const { data, width, height } = ctx.getImageData(0, 0, image.width, image.height)

  // The unit's shape is either a white silhouette (opaque matte) or an opaque cutout on a
  // transparent background. Trace the alpha channel when the image uses transparency, otherwise
  // fall back to brightness (the red channel of a grayscale matte).
  let transparent = false
  for (let i = 3; i < data.length; i += 4) {
    if (data[i] < 250) {
      transparent = true
      break
    }
  }
  const solid = new Uint8ClampedArray(width * height)
  for (let p = 0; p < solid.length; p++) {
    const i = p * 4
    solid[p] = transparent ? data[i + 3] : data[i]
  }

  const contourPoints = MarchingSquares(solid, width, height, threshold)

  // Validate contour closed properly
  if (contourPoints.length < 3) return null

  const simplified = DouglasPeucker(contourPoints, simplifyTolerance)

  // Convert to GeoJSON coordinates [lng, lat] format for Leaflet Simple CRS.
  // In Simple CRS, unproject([x, y], zoom) returns LatLng(-y, x) at zoom 0, so we negate y.
  const coordinates = simplified.map(([x, y]) => [x, -y])

  // Close the polygon (first point = last point)
  if (
    coordinates.length > 0 &&
    (coordinates[0][0] !== coordinates[coordinates.length - 1][0] ||
      coordinates[0][1] !== coordinates[coordinates.length - 1][1])
  ) {
    coordinates.push([...coordinates[0]])
  }

  return {
    type: 'Feature',
    properties: {},
    geometry: {
      type: 'Polygon',
      coordinates: [coordinates],
    },
  }
}

const MarchingSquares = (solid, width, height, threshold) => {
  const isSolid = (x, y) => {
    if (x < 0 || x >= width || y < 0 || y >= height) return false
    return solid[y * width + x] > threshold
  }

  // Find starting point: first solid pixel scanning from top-left
  let startX = -1,
    startY = -1
  outer: for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      if (isSolid(x, y)) {
        startX = x
        startY = y
        break outer
      }
    }
  }

  if (startX === -1) return []

  // Moore neighbor tracing algorithm
  // 8-directional neighbors: starting from left, going clockwise
  // 0=left, 1=up-left, 2=up, 3=up-right, 4=right, 5=down-right, 6=down, 7=down-left
  const dx = [-1, -1, 0, 1, 1, 1, 0, -1]
  const dy = [0, -1, -1, -1, 0, 1, 1, 1]

  const contour = []
  let x = startX,
    y = startY
  let backtrack = 4 // Start looking from the right (opposite of where we came from)

  do {
    contour.push([x, y])

    // Search for next boundary pixel, starting from backtrack direction going clockwise
    let found = false
    for (let i = 0; i < 8; i++) {
      const dir = (backtrack + i) % 8
      const nx = x + dx[dir]
      const ny = y + dy[dir]

      if (isSolid(nx, ny)) {
        // Found next boundary pixel
        backtrack = (dir + 4 + 1) % 8 // Start next search from opposite direction + 1
        x = nx
        y = ny
        found = true
        break
      }
    }

    if (!found) break
    if (contour.length > width * height) break
  } while (!(x === startX && y === startY && contour.length > 1))

  return contour
}

const DouglasPeucker = (points, tolerance) => {
  if (points.length <= 2) return points

  let maxDist = 0
  let maxIdx = 0
  const first = points[0]
  const last = points[points.length - 1]

  for (let i = 1; i < points.length - 1; i++) {
    const dist = PerpendicularDistance(points[i], first, last)
    if (dist > maxDist) {
      maxDist = dist
      maxIdx = i
    }
  }

  if (maxDist > tolerance) {
    const left = DouglasPeucker(points.slice(0, maxIdx + 1), tolerance)
    const right = DouglasPeucker(points.slice(maxIdx), tolerance)
    return left.slice(0, -1).concat(right)
  }

  return [first, last]
}

const PerpendicularDistance = (point, lineStart, lineEnd) => {
  const [x, y] = point
  const [x1, y1] = lineStart
  const [x2, y2] = lineEnd

  const dx = x2 - x1
  const dy = y2 - y1
  const len = Math.sqrt(dx * dx + dy * dy)

  if (len === 0) return Math.sqrt((x - x1) ** 2 + (y - y1) ** 2)

  return Math.abs(dy * x - dx * y + x2 * y1 - y2 * x1) / len
}
